import React, { useContext } from 'react'
import { NotificationManagerContext, Spacer, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import { Link } from 'react-router-dom'
import DiscussionStatusBanner from 'src/apps/Discussions/components/StatusBanner'
import FileListItemRowSmall from 'src/apps/Files/components/FileListItemRowSmall'
import InitiativeMoreButton from 'src/apps/Initiative/components/MoreButton'
import InitiativeSubscribeButton from 'src/apps/Initiative/components/SubscribeButton'
import InitiativeTypeLabel from 'src/apps/Initiative/components/TypeLabel'
import MetadataBlock from 'src/apps/Initiative/components/MetadataBlock'
import MessageFeed from 'src/apps/Messages/components/Feed'
import Footer from 'src/components/Footer'
import { downloadFileAsset } from 'src/components/utils'
import RichText from 'src/components/utils/RichText'
import stylesheet from 'src/components/utils/RichText/stylesheet'
import { useOrganization, useUpdateInitiative } from 'src/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'
import ThemeContext, { Theme } from 'src/libraries/contexts/Theme'

const DiscussionView = ({ children, displayMode }) => {
   const isFullscreen = displayMode === 'fullscreen'
   const { showNotification } = useContext(NotificationManagerContext)
   const theme = useContext(ThemeContext)

   const { organization } = useOrganization()
   const { initiative } = useContext(InitiativeHierarchyContext)
   const { updateInitiative } = useUpdateInitiative()

   const avatarUrl = initiative.addedBy.avatarMedia
      ? initiative.addedBy.avatarMedia?.file.thumbnailUrlW48
      : '/assets/images/avatarPlaceholder.svg'

   const initiativeDescription = initiative?.body?.elements?.[0]?.body || ''
   const isDraft = initiative.privacy === 'draft'

   // TODO: Improve this later
   // We check that the second block (element[1]) is a files block
   // (this is to make sure legacy GNS3 discussions dont blow up)
   // See PR #551
   const hasFileAttachments = initiative.body.elements?.[1]?.__typename === 'FileListElement' &&
      initiative.body.elements?.[1]?.fileInstances.length > 0

   const hasTags = initiative.tags.length > 0

   const handleUpdateInitiative = async (value) => {
      try {
         await updateInitiative({ id: initiative.id }, value)

         showNotification({
            message: `${initiative.type.nameSingular} updated successfully`,
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         showNotification({
            message: err.message,
            title: 'Error!',
            type: 'error'
         })
      }
   }

   return (
      <>
         {!isFullscreen && (
            <Theme globalNavIsHidden />
         )}
         <Helmet title={`${initiative.name} | ${organization.name}`} />
         <View
            paddingTop={isFullscreen && theme.viewportMarginTop}
            paddingLeft={theme.viewportMarginLeft}
            paddingRight={theme.viewportMarginRight}>
            <DiscussionStatusBanner />
            <View
               display="flex"
               justifyContent="center"
               width="100%">
               <View
                  width="100%"
                  maxWidth="1000px"
                  padding="50px 25px[a] 70px 50px[b-f]">
                  <View
                     float="left"
                     width="100%[a-b] calc(100% - 160px)[c-f]"
                     paddingRight="25px[c-f]"
                     borderRight="1px solid #eee[c-f]">
                     <View
                        float="left"
                        display="block"
                        width="100%"
                        minHeight="120vh"
                        paddingBottom="90px">
                        <View float="left" width="100%">
                           <View flex="1 1 auto" width="100%">
                              <InitiativeTypeLabel />
                              <View paddingRight="5%">
                                 <Text size="9[a-b] 10[c-f]" weight="medium">
                                    {initiative.name}
                                 </Text>
                              </View>
                              <Spacer size="1[a-b] 2[c-f]" />
                              <View paddingBottom="50px">
                                 <Text color="#888" size="1.5">
                                    {isDraft && (
                                       <View
                                          backgroundColor="rgba(255, 235, 131, 0.2)"
                                          borderRadius="2px"
                                          display="inline-block"
                                          padding="2px 10px">
                                          <Text
                                             style={{ display: 'inline-block' }}
                                             color="rgba(163, 117, 75, 1)"
                                             size="1.5"
                                             weight="medium">
                                             Draft
                                          </Text>
                                       </View>
                                    )}
                                    {!isDraft && (
                                       <View display="inline-block">
                                          {!initiative.discussionStatus.closed && (
                                             <Text
                                                color="#444"
                                                size="1.5"
                                                weight="medium">
                                                Open Discussion
                                             </Text>
                                          )}
                                          {initiative.discussionStatus.closed && (
                                             <Text
                                                color={initiative.discussionFormat === 'qa'
                                                   ? '#2fb949'
                                                   : '#666'
                                                }
                                                size="1.5"
                                                weight="medium">
                                                {initiative.discussionFormat === 'qa'
                                                   ? 'Answered '
                                                   : 'Closed'
                                                }
                                             </Text>
                                          )}
                                       </View>
                                    )}
                                    &nbsp;&nbsp;&#183;&nbsp;&nbsp;
                                    {initiative.dateAdded}
                                 </Text>
                              </View>
                              {initiativeDescription && (
                                 <View float="left" width="100%" paddingBottom="50px">
                                    <Text size="2.5[a-b] 3[c-f]" lineHeight="140%" color="#444">
                                       <RichText
                                          html={initiativeDescription}
                                          stylesheet={stylesheet()}
                                       />
                                    </Text>
                                 </View>
                              )}
                              {isDraft && !initiativeDescription && (
                                 <View float="left" width="100%" paddingBottom="50px">
                                    <Text size="2.5[a-b] 3[c-f]" lineHeight="150%" color="#aaa">
                                       No description provided
                                    </Text>
                                 </View>
                              )}
                              {/*
                                 // TODO: Maybe convert this into a reuseable component
                                 into the future
                              */}
                              {hasFileAttachments && (
                                 <View
                                    float="left"
                                    width="100%"
                                    borderTop="1px solid #e5e5e5"
                                    borderLeft="1px solid #e5e5e5"
                                    borderRight="1px solid #e5e5e5">
                                    {initiative.body.elements[1].fileInstances.map(instance => (
                                       <FileListItemRowSmall
                                          key={instance.file.id}
                                          id={instance.file.id}
                                          infected={instance.file.infected}
                                          mimetypeMismatch={instance.file.mimetypeMismatch}
                                          mimetypeVerified={instance.file.mimetypeVerified}
                                          name={instance.file.name}
                                          onDownloadButtonClick={() => {
                                             downloadFileAsset(instance.file.id)
                                          }}
                                          paddingHorizontal="10px"
                                          showDownloadButton
                                          status={instance.file.status}
                                          thumbnailUrl={instance.file.thumbnailUrlW48}
                                       />
                                    ))}
                                 </View>
                              )}
                              {hasTags && (
                                 <View
                                    float="left"
                                    width="100%"
                                    marginBottom="20px"
                                    paddingTop="20px"
                                    borderTop={!hasFileAttachments && '1px solid #ececec'}>
                                    <View maxWidth="650px" marginTop="10px">
                                       {initiative.tags.map(tag => (
                                          <View
                                             key={tag}
                                             display="inline-flex"
                                             alignItems="center"
                                             height="25px"
                                             padding="0px 10px"
                                             borderRadius="2px"
                                             marginRight="10px"
                                             marginBottom="10px"
                                             backgroundColor="#e5e5e5">
                                             <Text size="2" color="#888">{tag}</Text>
                                          </View>
                                       ))}
                                    </View>
                                 </View>
                              )}
                              <View
                                 float="left"
                                 width="100%"
                                 margin="0px 0px 50px 0px"
                                 paddingTop="25px"
                                 borderTop={(hasFileAttachments && !hasTags)
                                    ? 'none'
                                    : '1px solid #eee'}
                                 display="flex"
                                 flexFlow="column[a-b] row[c-f]"
                                 justifyContent="space-between">
                                 <View
                                    display="flex"
                                    alignItems="center"
                                    flex="1 1 auto"
                                    paddingBottom="25px[a-b]">
                                    <View
                                       flex="0 0 auto"
                                       float="left"
                                       width="30px"
                                       height="30px"
                                       borderRadius="50%"
                                       backgroundColor="#ddd"
                                       backgroundImage={`url(${avatarUrl})`}
                                       backgroundSize={initiative.addedBy.avatarMedia
                                          ? 'cover'
                                          : '21px 21px'
                                       }
                                       backgroundPosition="bottom center"
                                       backgroundRepeat="no-repeat"
                                       marginRight="15px"
                                    />
                                    <Text color="#888" size="1.5">
                                       Posted by
                                       &nbsp;
                                       <Link to={`/profile/${initiative.addedBy.id}`}>
                                          <Text
                                             style={{ display: 'inline-block' }}
                                             color="#222"
                                             size="1.5[a-d] 2[e-f]"
                                             weight="medium">
                                             {initiative.addedBy.fullName}
                                          </Text>
                                       </Link>
                                       <br />
                                       {initiative.dateAdded}
                                    </Text>
                                 </View>
                                 <View
                                    position="relative"
                                    flex="0 0 auto"
                                    display="flex"
                                    alignItems="center">
                                    {!isDraft && (
                                       <InitiativeSubscribeButton />
                                    )}
                                    <Spacer size="1" orientation="vertical" />
                                    <InitiativeMoreButton />
                                 </View>
                              </View>
                           </View>
                        </View>
                        <View float="left" width="100%">
                           <View
                              width="100%"
                              borderBottom="1px solid #eee"
                              paddingBottom="20px">
                              <Text size="2[a-d] 3[e-f]" weight="medium">
                                 Comments
                              </Text>
                           </View>
                           {isDraft && (
                              <View
                                 backgroundColor="#f5f5f5"
                                 padding="60px"
                                 textAlign="center">
                                 <Text size="1.5[a-b] 2[c-f]" color="#888">
                                    {`Comments are not available while this ${initiative.type.nameSingular} is in draft mode.`}
                                 </Text>
                              </View>
                           )}
                           {!isDraft && (
                              <MessageFeed
                                 allowFileAttachments
                                 allowMarkClosingMessage={!initiative.discussionStatus.closed &&
                                    initiative.discussionFormat === 'qa'}
                                 allowMessageCreate={!initiative.discussionStatus.closed}
                                 closingMessageId={initiative.discussionStatus.closingMessageId}
                                 currentUserCanModerate={initiative.currentUserCanModerate}
                                 displayMode="conversation"
                                 hasUnreadEntries={initiative.thread.hasUnreadEntries}
                                 messageLastViewed={initiative.thread.messageLastViewed}
                                 messageInputToolbarConfig="advanced"
                                 onCloseMessageButtonClick={(closingMessageId) => {
                                    handleUpdateInitiative({
                                       discussionStatus: {
                                          closed: true,
                                          closingMessageId
                                       }
                                    })
                                 }}
                              />
                           )}
                        </View>
                        {initiative.discussionStatus.closed && (
                           <View
                              display="flex"
                              justifyContent="center"
                              float="left"
                              width="100%"
                              backgroundColor="#f5f5f5"
                              padding="24px">
                              <Text size="1.5" color="#555">
                                 <Link to={`/profile/${initiative.discussionStatus.closedBy.id}`}>
                                    <b style={{ color: '#222' }}>
                                       {initiative.discussionStatus.closedBy.fullName}
                                    </b>
                                 </Link>
                                 {` closed this discussion on ${initiative.discussionStatus.dateClosed}`}
                              </Text>
                           </View>
                        )}
                        {children}
                     </View>
                  </View>
                  <View float="left" width="160px[c-f]">
                     <View
                        display="none[a-b] block[c-f]"
                        height="100%"
                        paddingLeft="25px">
                        <MetadataBlock initiative={initiative} />
                     </View>
                  </View>
               </View>
            </View>
         </View>
         {isFullscreen && <Footer backgroundColor="#111" />}
      </>
   )
}

DiscussionView.propTypes = {
   children: PropTypes.node,
   displayMode: PropTypes.oneOf(['fullscreen', 'modal'])
}

DiscussionView.defaultProps = {
   children: null,
   displayMode: 'fullscreen'
}

export default DiscussionView
